
import {
	Component,
	Emit,
	Prop,
	Watch,
} from 'vue-property-decorator';
import Vue from 'vue';

const TEST_ID = 'Kloans-InputSlider';

@Component
/**
 * This component takes any child component for its default slot.
 * The child component can have a prop of :value and outputs @input, @blur, @focus, @enter.
 * It can be extended to use any other props and outputs
 * This decouples the dollar input from the input slider completely.
 *
 * @example
 * ```ts
 * 	<InputSlider
 *		:min="nextPaymentInputMin"
*		:max="nextPaymentInputMax"
*		:error-message="nextPaymentInputErrorMesasge"
*		:tooltip="nextPaymentInputTooltip"
*		@change="onNextPaymentInputChange"
*		@input="onNextPaymentInputInput"
*		@blur="onNextPaymentInputBlur"
*	>
*	<template #default="{enter, blur, input, value}">
*		<DollarInput
*			:value="value"
*			@input="input"
*			@blur="blur"
*			@enter="enter"
*		/>
*	</template>
*
*	</InputSlider>
* ```
*/
export default class InputSlider extends Vue {
	@Prop()
	public append!: string;

	@Prop({ default: TEST_ID })
	public dataTestid!: string;

	public debouncedTooltip: string | null = null;

	@Prop()
	public errorMessage!: string | null;

	@Prop()
	public hint!: string;

	@Prop()
	public id!: string;

	public inputValue: number = 0;

	@Prop()
	public messages!: string | string[];

	@Prop({ required: true })
	public max!: number;

	@Prop({ required: true })
	public min!: number;

	@Prop()
	public prepend!: string;

	public showTooltip = false;

	public sliderValue: number = 0;

	@Prop()
	public tooltip!: string | null;

	@Prop()
	public value!: number;

	private isSliding: boolean = false;

	@Emit('blur')
	public emitBlur(): number {
		return this.inputValue;
	}

	@Emit('change')
	public emitChange(): number {
		return this.inputValue;
	}

	@Emit('focus')
	public emitFocus(): number {
		return this.inputValue;
	}

	@Emit('input')
	public emitInput(): number {
		return this.inputValue;
	}

	public onInputBlur(value: number): void {
		this.inputValue = value;
		this.sliderValue = value;
		this.emitBlur();
		this.emitChange();
	}

	public onInputEnter(value: number): void {
		this.inputValue = value;
		this.sliderValue = value;
		this.emitChange();
	}

	public onInputInput(value: number): void {
		this.inputValue = value;
		this.sliderValue = value;
		this.emitInput();
		this.emitChange();
	}

	public onSliderMousedown(): void {
		this.isSliding = true;
	}

	public onSliderMouseup(): void {
		this.isSliding = false;
		this.inputValue = this.sliderValue;
		this.emitChange();
	}

	@Watch('sliderValue')
	public watchSliderValue(value: number): void {
		if (this.isSliding) {
			this.inputValue = value;
		}
	}

	@Watch('tooltip')
	public watchTooltip(tooltip: string | null): void {
		if (!tooltip) {
			this.showTooltip = false;

			// Used to hide the tooltip before its animation delay removes the text and shows a small blank tooltip
			// using nextTick does not work here, the delay needs to be longer
			// This has no impact on the user since the tooltip is hidden before the timeout takes effect
			setTimeout(() => {
				this.debouncedTooltip = tooltip;
			}, 500);

			return;
		}

		this.debouncedTooltip = tooltip;
		this.showTooltip = true;
	}

	@Watch('value')
	public watchValue(value: number) {
		this.inputValue = value;
		this.sliderValue = value;
	}
}
